import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GeometryType } from "@evergis/api";
import { useMapContext } from "@evergis/react";
import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { Checkbox, Description, Switch } from "@evergis/ui";

import { PopupContainer, SettingsDoubleSwitch, SettingsLink, SwitchLabel, SwitchWrapper } from "./styled";

import { ShadowedPointSymbol } from "../../../../react-sgis/symbols/editorSymbols/ShadowedPointSymbol";
import { ShadowedPolySymbol } from "../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";

import { useGeometrySelectionContext } from "../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useEditCoordinatesDialog } from "../../../../hooks/useDialogs";
import { unionFeaturesRings } from "./Selection/hooks/useSelectionEdit";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./Selection/hooks/useSelectionSymbols";
import { createPointFeature } from "./Selection/utils/createPointFeature";
import { createPolygonFeature } from "./Selection/utils/createPolygonFeature";
import { createPolylineFeature } from "./Selection/utils/createPolylineFeature";
import { useGeometrySelection } from "../../../../hooks/geometrySelection/useGeometrySelection";

import { GeometrySelectionTypes } from "./types";

// eslint-disable-next-line max-lines-per-function
export const Settings: FC = () => {
  const { t } = useTranslation("mapControls");
  const [geometrySelection, setGeometrySelection] = useGeometrySelection();
  const { selectOutside, bufferInclude } = geometrySelection.settings || {};
  const { setFeatures } = useGeometrySelectionContext();
  const openEditCoordinatesDialog = useEditCoordinatesDialog();
  const { map } = useMapContext();
  const symbols = useSelectionSymbols();

  const onChangeSelectOutside = useCallback(
    (value?: boolean) => {
      setGeometrySelection({
        settings: {
          ...geometrySelection.settings,
          selectOutside: value ?? !geometrySelection.settings?.selectOutside,
        },
      });
    },
    [geometrySelection, setGeometrySelection],
  );

  const onChangeBufferInclude = useCallback(() => {
    setGeometrySelection({
      ...geometrySelection,
      settings: {
        ...geometrySelection.settings,
        bufferInclude: !geometrySelection.settings?.bufferInclude,
      },
    });
  }, [geometrySelection, setGeometrySelection]);

  const onEditFinish = useCallback(
    (coordinates: Coordinates | Coordinates[][] | null, geometryType: GeometryType) => {
      let processedFeatures;

      switch (geometryType) {
        case GeometryType.Point:
          processedFeatures = createPointFeature(
            coordinates as Coordinates,
            map.crs,
            symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
          );
          break;
        case GeometryType.Polygon:
          processedFeatures = coordinates.map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            ),
          );
          break;
        case GeometryType.Polyline:
          processedFeatures = coordinates.map(contour =>
            createPolylineFeature(
              [contour],
              map.crs,
              map.resolution,
              symbols[GeometrySelectionSymbolType.polygonEdit] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polylineEdit] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polylineCreate] as ShadowedPolySymbol,
            ),
          );
          break;
        default:
          return;
      }

      setFeatures(processedFeatures);
    },
    [setFeatures, map.crs, map.resolution],
  );

  const onEditCoordinates = useCallback(() => {
    let geometryType;
    let geometry;

    switch (geometrySelection.type) {
      case GeometrySelectionTypes.Point:
        geometryType = GeometryType.Point;
        geometry = geometrySelection.history.features[geometrySelection.history.step][0]._position;
        break;
      case GeometrySelectionTypes.Line:
      case GeometrySelectionTypes.Route:
        geometryType = GeometryType.Polyline;
        geometry = unionFeaturesRings(geometrySelection.history.features[geometrySelection.history.step]);
        break;
      case GeometrySelectionTypes.Circle:
      case GeometrySelectionTypes.Rect:
      case GeometrySelectionTypes.Polygon:
        geometryType = GeometryType.Polygon;
        geometry = unionFeaturesRings(geometrySelection.history.features[geometrySelection.history.step]);
        break;
      default:
        return;
    }

    openEditCoordinatesDialog([], {
      geometry,
      geometryType,
      editGeometry: onEditFinish,
      geometryTypeChoiceDisabled: true,
      crs: map.crs,
    });
  }, [onEditFinish, openEditCoordinatesDialog, geometrySelection.history.features, geometrySelection.type, map.crs]);

  return (
    <PopupContainer width="22rem">
      <Description>{t("featureSelectionArea")}</Description>
      <SettingsDoubleSwitch>
        <SwitchLabel active={!geometrySelection.settings?.selectOutside} onClick={() => onChangeSelectOutside(false)}>
          {t("insideFigure")}
        </SwitchLabel>
        <SwitchWrapper>
          <Switch isOn checked={Boolean(selectOutside)} onChange={() => onChangeSelectOutside()} />
        </SwitchWrapper>
        <SwitchLabel active={!!geometrySelection.settings?.selectOutside} onClick={() => onChangeSelectOutside(true)}>
          {t("outsideFigure")}
        </SwitchLabel>
      </SettingsDoubleSwitch>
      {geometrySelection.isBuffer && (
        <>
          <Description>{t("bufferSelection")}</Description>
          <Checkbox
            label={t("includeBufferArea")}
            checked={bufferInclude === undefined || Boolean(bufferInclude)}
            onChange={onChangeBufferInclude}
          />
        </>
      )}
      {geometrySelection.history.features.length > 0 && (
        <>
          <Description>{t("geometryParameters")}</Description>
          <SettingsLink onClick={onEditCoordinates}>{t("editCoordinates")}</SettingsLink>
        </>
      )}
    </PopupContainer>
  );
};
